import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuthStore } from "@/store/authStore";
import { fetchMe } from "@/services/auth";
import { apiConfig } from "@/services/api";

/** 权限 / 角色在后端改过后，已登录的页面最迟这么久会自己刷新一次（毫秒）。 */
const ME_REFRESH_MS = 5 * 60_000;

export function AuthGuard({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const loggedIn = !!user;

  // RBAC-ENF1 · 进入后台时 / 每 5 分钟 / 切回窗口时，用 /auth/me 刷新权限点（旧会话没有 permissions 字段，靠这一步补上）。
  // 失败（断网 / 401 由 api 层处理）不影响使用，沿用已有权限。
  useEffect(() => {
    if (!loggedIn || apiConfig.useMock) return;
    let alive = true;
    const refresh = () => {
      fetchMe()
        .then((me) => {
          if (alive) useAuthStore.getState().setPermissions(me.permissions ?? [], me.roleCodes, me.roleLabels);
        })
        .catch(() => undefined);
    };
    refresh();
    const timer = window.setInterval(refresh, ME_REFRESH_MS);
    const onVisible = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loggedIn]);

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}
